/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { submitFeedback } from '../services/feedbackApi';
import Modal from './ui/Modal';
import ErrorMessage from './ui/ErrorMessage';
import useImageProcessor from '../hooks/useImageProcessor';
import {
  containerVariants, itemVariants, headerVariants, successVariants,
} from '../utils/animations';

function FeedbackModal({ onClose }) {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [type, setType] = useState('general');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  const { isProcessing: isProcessingImage, error: imageError, processImage } = useImageProcessor();

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const result = await processImage(e.target.files[0]);
      if (result) {
        setScreenshot(result.base64);
      }
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!message.trim()) {
      setSubmitError('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitFeedback({
        type,
        message: message.trim(),
        email: email.trim(),
        pageUrl: window.location.origin + location.pathname,
        screenshot,
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Combine errors for display
  const displayError = submitError || imageError;

  return (
    <Modal onClose={onClose} maxWidth="max-w-sm">
      {/* Header */}
      <motion.div
        className="flex justify-between items-center mb-6"
        variants={headerVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className="text-xl font-semibold text-puzzle-text">Send Feedback</h2>
        <button
          onClick={onClose}
          type="button"
          className="p-1 text-puzzle-text-secondary hover:text-puzzle-text transition-colors"
          aria-label="Close feedback form"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </motion.div>

      {success ? (
        <motion.div
          className="text-center py-8"
          variants={successVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="text-puzzle-green text-5xl mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-puzzle-text font-medium">Thank you for your feedback!</p>
          <p className="text-puzzle-text-secondary text-sm mt-1">We appreciate you taking the time to help us improve.</p>
        </motion.div>
      ) : (
        <motion.form
          onSubmit={handleSubmit}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Feedback Type */}
          <motion.div className="mb-4" variants={itemVariants}>
            <label htmlFor="feedback-type" className="block text-sm font-medium text-puzzle-text mb-2">
              Feedback Type
            </label>
            <select
              id="feedback-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-2 border border-puzzle-border rounded-lg bg-puzzle-surface text-puzzle-text focus:outline-none focus:ring-2 focus:ring-puzzle-blue"
            >
              <option value="general">General Feedback</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="other">Other</option>
            </select>
          </motion.div>

          {/* Message */}
          <motion.div className="mb-4" variants={itemVariants}>
            <label htmlFor="feedback-message" className="block text-sm font-medium text-puzzle-text mb-2">
              Your Feedback
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think..."
              rows={4}
              maxLength={2000}
              className="w-full px-4 py-2 border border-puzzle-border rounded-lg bg-puzzle-surface text-puzzle-text placeholder-puzzle-text-secondary focus:outline-none focus:ring-2 focus:ring-puzzle-blue resize-none"
            />
            <p className="text-xs text-puzzle-text-secondary mt-1 text-right">
              {message.length}
              /2000
            </p>
          </motion.div>

          {/* Email (only for guests) */}
          {!user && (
          <motion.div className="mb-4" variants={itemVariants}>
            <label htmlFor="feedback-email" className="block text-sm font-medium text-puzzle-text mb-2">
              Email
              {' '}
              <span className="text-puzzle-text-secondary font-normal">(optional, for follow-up)</span>
            </label>
            <input
              id="feedback-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-2 border border-puzzle-border rounded-lg bg-puzzle-surface text-puzzle-text placeholder-puzzle-text-secondary focus:outline-none focus:ring-2 focus:ring-puzzle-blue"
            />
          </motion.div>
          )}

          {/* Screenshot Upload */}
          <motion.div className="mb-4" variants={itemVariants}>
            <p className="block text-sm font-medium text-puzzle-text mb-2">
              Screenshot
              {' '}
              <span className="text-puzzle-text-secondary font-normal">(optional)</span>
            </p>
            {screenshot ? (
              <div className="relative inline-block">
                <img
                  src={screenshot}
                  alt="Screenshot preview"
                  className="max-h-32 rounded-lg border border-puzzle-border"
                />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  aria-label="Remove screenshot"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="screenshot-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessingImage}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-puzzle-border rounded-lg text-puzzle-text hover:bg-puzzle-hover transition-colors disabled:opacity-50"
                >
                  {isProcessingImage ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Add Screenshot
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>

          {/* Error Message */}
          {displayError && (
          <motion.div className="mb-4" variants={itemVariants}>
            <ErrorMessage message={displayError} />
          </motion.div>
          )}

          {/* Submit Button */}
          <motion.div variants={itemVariants}>
            <motion.button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-puzzle-blue text-white font-medium rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </motion.button>
          </motion.div>
        </motion.form>
      )}
    </Modal>
  );
}

FeedbackModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default FeedbackModal;
